import React from 'react';
import { PaymentCapabilityStatus } from '../services/identityVerification';

const SUPPORT_EMAIL = 'support@clero.money';

export const openSupportEmail = () => {
  window.location.href = `mailto:${SUPPORT_EMAIL}`;
};

export const IdentityVerificationBlockedCard: React.FC<{
  onLearnMore?: () => void;
  status?: PaymentCapabilityStatus;
  primaryActionLabel?: string;
  onPrimaryAction?: () => void;
  compact?: boolean;
}> = ({ onLearnMore, status = 'permanently_rejected', primaryActionLabel, onPrimaryAction, compact = false }) => {
  const copy = status === 'document_required'
    ? {
        bannerTitle: 'Further verification needed',
        bannerBody: 'We couldn’t verify your identity yet and need more information to continue payments',
      }
    : status === 'pending'
    ? {
        bannerTitle: 'Verification in review',
        bannerBody: 'Astra is reviewing your identity. Payments unlock after verification finishes',
      }
    : status === 'failed_ach_unresolved'
    ? {
        bannerTitle: 'Payment failed',
        bannerBody: 'Retry your failed bank payment to keep using payments in Clero',
      }
    : {
        bannerTitle: 'Payments are unavailable',
        bannerBody: 'We couldn’t verify your identity',
      };

  return (
  <div className={`rounded-[24px] border border-amber-200 bg-amber-50 ${compact ? 'p-4' : 'p-5'} flex items-start gap-3`}>
    <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 text-lg">!</div>
    <div className="min-w-0 flex-1">
      <p className="text-[13px] font-black uppercase tracking-[0.14em] text-amber-800">{copy.bannerTitle}</p>
      <p className="mt-1 text-sm text-amber-900">{copy.bannerBody}</p>
    </div>
    {onPrimaryAction ? (
      <button
        type="button"
        onClick={onPrimaryAction}
        className="shrink-0 rounded-xl bg-teal-600 px-3 py-2 text-sm font-bold text-white hover:bg-teal-700"
      >
        {primaryActionLabel || (status === 'document_required' ? 'Continue' : (status === 'failed_ach_unresolved' ? 'Review payment' : 'Learn more'))}
      </button>
    ) : null}
    {onLearnMore ? (
      <button
        type="button"
        onClick={onLearnMore}
        className="shrink-0 rounded-xl px-3 py-2 text-sm font-bold text-amber-800 hover:bg-amber-100"
      >
        {status === 'document_required' || status === 'failed_ach_unresolved' ? 'Contact support' : 'Learn more'}
      </button>
    ) : null}
  </div>
  );
};

export const IdentityVerificationBlockedModal: React.FC<{
  visible: boolean;
  onClose: () => void;
  status?: PaymentCapabilityStatus;
  onPrimaryAction?: () => void;
  primaryActionBusy?: boolean;
  errorMessage?: string | null;
}> = ({ visible, onClose, status = 'permanently_rejected', onPrimaryAction, primaryActionBusy = false, errorMessage = null }) => {
  if (!visible) return null;

  const copy = status === 'document_required'
    ? {
        title: 'We couldn’t verify your identity yet',
        body: 'We need a bit more verification before you can continue payments with Clero.\n\nAstra asked for a government-issued ID.',
        primaryLabel: 'Continue',
      }
    : status === 'pending'
    ? {
        title: 'Verification is taking longer than expected',
        body: 'Astra is still reviewing your identity. Refresh your status to check whether verification has finished.',
        primaryLabel: 'Refresh status',
      }
    : status === 'failed_ach_unresolved'
    ? {
        title: 'A bank payment failed',
        body: 'You need to successfully retry your failed payment before you can send more bank payments or create new payment-enabled splits in Clero.',
        primaryLabel: 'Review payment',
      }
    : {
        title: 'We couldn’t verify your identity',
        body: 'This means you won’t be able to send or receive payments with Clero. You can still use Clero to track and manage splits.',
        primaryLabel: 'Contact support',
      };

  return (
    <div className="fixed inset-0 z-[99] bg-slate-950/45 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md rounded-[32px] border border-slate-200 bg-white px-6 py-7 shadow-[0_28px_70px_rgba(15,23,42,0.2)]">
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="h-10 w-10 rounded-full bg-slate-100 text-slate-500"
          >
            ×
          </button>
        </div>
        <div className="mx-auto mt-1 h-16 w-16 rounded-[22px] bg-amber-100 flex items-center justify-center text-[28px] text-amber-700">!</div>
        <h3 className="mt-5 text-center text-[28px] leading-[1.1] font-black tracking-tight text-slate-900">
          {copy.title}
        </h3>
        <p className="mt-4 whitespace-pre-line text-center text-[15px] leading-6 text-slate-600">
          {copy.body}
        </p>
        {errorMessage ? (
          <p role="alert" className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-center text-[13px] font-semibold text-rose-700">
            {errorMessage}
          </p>
        ) : null}
        {(status === 'pending' || status === 'document_required' || status === 'failed_ach_unresolved') && onPrimaryAction ? (
          <div className="mt-7 space-y-3">
            <button
              type="button"
              onClick={onPrimaryAction}
              disabled={primaryActionBusy}
              className="h-12 w-full rounded-2xl bg-teal-600 text-sm font-black text-white hover:bg-teal-700 disabled:bg-slate-200 disabled:text-slate-500"
            >
              {primaryActionBusy ? 'Refreshing…' : copy.primaryLabel}
            </button>
            <button
              type="button"
              onClick={openSupportEmail}
              className="h-12 w-full rounded-2xl border border-amber-200 bg-amber-50 text-sm font-black text-amber-800 hover:bg-amber-100"
            >
              Contact support
            </button>
          </div>
        ) : status === 'permanently_rejected' ? (
          <div className="mt-7 space-y-3">
            <button
              type="button"
              onClick={openSupportEmail}
              className="h-12 w-full rounded-2xl bg-teal-600 text-sm font-black text-white hover:bg-teal-700"
            >
              Contact support
            </button>
            <button
              type="button"
              onClick={onClose}
              className="h-12 w-full rounded-2xl bg-slate-100 text-sm font-black text-slate-700 hover:bg-slate-200"
            >
              Got it
            </button>
          </div>
        ) : (
          <button type="button" onClick={onClose} className="mt-7 h-12 w-full rounded-2xl bg-teal-600 text-sm font-black text-white hover:bg-teal-700">
            Close
          </button>
        )}
      </div>
    </div>
  );
};
